<template>
  <v-card class="card">
    <v-tabs v-model="tab" class="align-center" density="default" height="64">
      <v-tab ripple value="registeredUsers">Registered Users</v-tab>
      <v-tab v-if="isSuperAdmin" ripple value="organisations">Organizations</v-tab>
      <v-spacer></v-spacer>
      <v-text-field
        class="align-self-right mt-0 pt-0"
        append-inner-icon="mdi-magnify"
        v-model="search"
        label="Search"
        :single-line="true"
        hide-details
        variant="outlined"
        density="compact"
      >
      </v-text-field>
    </v-tabs>

    <v-window v-model="tab">
      <v-window-item value="registeredUsers">
        <div v-if="isSuperAdmin" class="d-flex flex-wrap align-center ga-3 px-1 pt-3 pb-3">
          <v-select
            v-model="roleFilter"
            :items="roleFilterOptions"
            label="Role"
            variant="outlined"
            density="compact"
            hide-details
            data-cy="role-filter"
            style="max-width: 190px"
          />
          <v-select
            v-model="orgFilter"
            :items="orgOptions"
            label="Organization"
            variant="outlined"
            density="compact"
            hide-details
            clearable
            style="max-width: 260px"
          />
          <v-select
            v-model="lastLoginFilter"
            :items="lastLoginOptions"
            label="Last login"
            variant="outlined"
            density="compact"
            hide-details
            style="max-width: 210px"
          />
          <span v-if="anyFilterActive" class="text-caption text-medium-emphasis">
            {{ filteredUsers.length }} of {{ users ? users.length : 0 }}{{ nextTokenUsers ? '+' : '' }}
          </span>
          <v-spacer />
          <v-btn
            color="primary"
            variant="flat"
            size="small"
            prepend-icon="mdi-shield-plus-outline"
            data-cy="add-admin-btn"
            @click="openAddAdminDialog"
          >
            Add admin
          </v-btn>
          <v-btn v-if="anyFilterActive" variant="text" size="small" @click="clearFilters">Clear filters</v-btn>
        </div>
        <v-card v-if="pendingInvites.length" variant="tonal" color="warning" class="mb-4" data-cy="pending-invites">
          <v-card-title class="text-subtitle-1 py-2 d-flex align-center">
            <v-icon size="small" class="mr-2">mdi-email-clock-outline</v-icon>
            Pending invitations ({{ pendingInvites.length }})
            <span class="text-caption text-medium-emphasis ml-3">Invited, but not yet logged in</span>
          </v-card-title>
          <v-divider />
          <v-list density="compact" bg-color="transparent">
            <v-list-item v-for="invite in pendingInvites" :key="invite.userId" :data-cy="`pending-${invite.email}`">
              <v-list-item-title>
                {{ pendingName(invite) }}
                <v-chip size="x-small" color="warning" variant="flat" class="ml-2">Pending</v-chip>
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ invite.email }}
                <span v-if="invite.createdAt" class="text-caption text-medium-emphasis ml-2">
                  invited {{ formatNoteDate(invite.createdAt) }}
                </span>
              </v-list-item-subtitle>
              <template v-slot:append>
                <v-btn
                  size="small"
                  variant="text"
                  prepend-icon="mdi-email-sync-outline"
                  :loading="resendingEmail === invite.email"
                  :data-cy="`resend-${invite.email}`"
                  @click="resend(invite.email)"
                >
                  Resend invite
                </v-btn>
              </template>
            </v-list-item>
          </v-list>
        </v-card>
        <v-data-table
          v-if="users"
          :headers="displayHeaders"
          :items="filteredUsers"
          :items-per-page="-1"
          hover
          v-model:expanded="expanded"
          show-expand
          :sort-by="tableSort"
          @update:sort-by="handleTableSort"
          :multi-sort="false"
          :loading="loadingUsers"
        >
          <template v-slot:item.hasNotes="{ item }">
            <v-badge
              v-if="item.adminNotes && item.adminNotes.length > 0"
              :content="item.adminNotes.length"
              color="primary"
              @click="toggleRowExpansion(item)"
              class="cursor-pointer"
            >
              <v-icon>mdi-note-text-outline</v-icon>
            </v-badge>
          </template>
          <template v-slot:item.firstname="{ item }">
            <div>
              {{
                (item.firstname + ' ' + item.lastname).length > 15 ? `${(item.firstname + ' ' + item.lastname).substring(0, 15)?.trim()}...` : (item.firstname + ' ' + item.lastname)
              }}
              <v-tooltip
                max-width="400px"
                location="top"
                activator="parent"
                v-if="(item.firstname + ' ' + item.lastname).length > 15"
              >
                {{ item.firstname + ' ' + item.lastname}}
              </v-tooltip>
              <v-chip v-if="item.isActive === false" size="x-small" color="error" variant="tonal" class="ml-1">
                Deactivated
              </v-chip>
            </div>
          </template>
          <template v-slot:item.platformRole="{ item }">
            <v-chip
              v-if="effectiveRole(item)"
              size="x-small"
              :color="roleColor(effectiveRole(item))"
              label
              data-cy="role-badge"
            >
              {{ effectiveRole(item) }}
            </v-chip>
            <span v-else class="text-disabled">—</span>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-menu location="bottom end">
              <template #activator="{ props }">
                <v-btn icon="mdi-dots-vertical" variant="text" size="small" data-cy="user-actions-btn" v-bind="props" />
              </template>
              <v-list density="compact" min-width="200">
                <v-list-item
                  v-if="item.isActive === false"
                  prepend-icon="mdi-account-check-outline"
                  title="Reactivate"
                  @click="toggleActive(item, true)"
                />
                <v-list-item
                  v-else
                  prepend-icon="mdi-account-off-outline"
                  title="Deactivate"
                  :disabled="isSelf(item)"
                  @click="toggleActive(item, false)"
                />
                <v-divider class="my-1" />
                <v-list-item
                  v-if="!isSuperAdminUser(item)"
                  prepend-icon="mdi-shield-crown-outline"
                  title="Make Admin"
                  @click="setRole(item, 'SuperAdmin', true)"
                />
                <v-list-item
                  v-else
                  prepend-icon="mdi-shield-off-outline"
                  title="Remove Admin"
                  :disabled="isSelf(item)"
                  @click="setRole(item, 'SuperAdmin', false)"
                />
                <v-divider class="my-1" />
                <v-list-item
                  prepend-icon="mdi-delete-outline"
                  title="Delete user…"
                  base-color="error"
                  :disabled="isSelf(item)"
                  @click="openDeleteDialog(item)"
                />
              </v-list>
            </v-menu>
          </template>
          <template v-slot:item.projectsAndMemberships="{ item }">
            {{ formatProjectsAndMemberships(item) }}
          </template>
          <template v-slot:item.subscription="{ item }">
            <v-chip
              v-if="subscriptionTypeByEmail[item.email]"
              :color="subscriptionTypeByEmail[item.email] === 'Enterprise' ? 'amber-accent-4' : 'deep-purple-accent-3'"
              size="small"
              label
            >
              {{ subscriptionTypeByEmail[item.email] }}
            </v-chip>
            <span v-else>-</span>
          </template>
          <template v-slot:item.lastLogin="{ item }">{{
            item.lastLogin && !item.lastLogin.startsWith('0000') ? item.lastLogin.substring(0, 10) : ''
          }}
          </template>
          <template v-slot:item.registrationDate="{ item }">
            {{ item.registrationDate ? item.registrationDate.substring(0, 10) : '' }}
          </template>
          <template v-slot:bottom></template>

          <template v-slot:expanded-row="{ item }">
            <td :colspan="displayHeaders.length">
              <v-card class="mx-4 mb-4 mt-4 elevation-3">
                <v-card-title class="text-subtitle-1">Organizations &amp; roles</v-card-title>
                <v-card-text>
                  <div v-if="loadingFootprint[item.id]" class="text-disabled">Loading…</div>
                  <div v-else-if="!(footprints[item.id] && footprints[item.id].length)" class="text-disabled">
                    Not a member of any organization.
                  </div>
                  <v-table v-else density="compact">
                    <thead>
                      <tr><th>Organization</th><th>Role</th><th>Members</th><th></th></tr>
                    </thead>
                    <tbody>
                      <tr v-for="f in footprints[item.id]" :key="f.orgId">
                        <td>{{ f.orgName }}</td>
                        <td>{{ f.role }}</td>
                        <td>{{ f.memberCount }}</td>
                        <td>
                          <v-chip v-if="f.isSoleOwner" size="x-small" color="warning" variant="tonal">Sole owner</v-chip>
                        </td>
                      </tr>
                    </tbody>
                  </v-table>
                </v-card-text>
              </v-card>
              <v-card class="mx-4 mb-4 elevation-3">
                <v-data-table
                  :headers="headersProjects"
                  :items="getProjects(item.organisations)"
                  :items-per-page="-1"
                  hover
                  hide-default-footer
                  class="elevation-1"
                >
                  <template v-slot:bottom></template>
                  <template v-slot:item.workflows="{ item, index }">
                    <v-dialog
                      v-model="dialog[index]"
                      width="600"
                      :scrim="false">
                      <template v-slot:activator="{ props }">
                        <a href="#" v-bind="props" @click.prevent>{{ item.workflows?.items ? item.workflows.items.length : 0 }}</a>
                      </template>
                      <v-card>
                        <v-layout fluid justify-space-between align-top>
                          <div>
                            <v-card-title
                              class="title-h5"
                              primary-title
                            >
                              Workflows for project '{{ item.name }}'
                            </v-card-title>
                          </div>
                          <v-spacer></v-spacer>
                          <div shrink>
                            <v-btn variant="plain" icon color="grey" @click="dialog[index] = false">
                              <v-icon>mdi-close</v-icon>
                            </v-btn>
                          </div>
                        </v-layout>
                        <v-card-text class="pa-0">
                          <v-data-table
                            :headers="headersWorkflows"
                            :items="item.workflows?.items || []"
                            :items-per-page="-1"
                            hover
                            hide-default-footer
                            class="elevation-1"
                            disable-pagination
                          >
                            <template v-slot:bottom></template>
                            <template v-slot:item.name="{ item }">
                              <div style="cursor: pointer;" @click="gotoWorkflow(item.id, item.projectId)">{{
                                item.name
                              }}
                              </div>
                            </template>
                            <template v-slot:item.eventsLength="{ item }">{{ item.domainEvents?.length }}</template>
                          </v-data-table>
                        </v-card-text>
                        <v-card-actions>
                          <v-spacer></v-spacer>
                          <v-btn
                            color="primary"
                            @click="dialog[index] = false">
                            Close
                          </v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-dialog>
                  </template>
                </v-data-table>
              </v-card>
              <v-card class="mx-4 mb-4 elevation-3">
                <v-card-title>Notes</v-card-title>
                <v-card-text>
                  <div class="mb-4">
                    <div class="d-flex align-start ga-4 mb-6">
                      <v-select
                        style="flex: 0 0 300px;"
                        v-model="newNoteTypes[item.id]"
                        :items="noteTypeOptions"
                        label="Note Type"
                        variant="outlined"
                        density="compact"
                        hide-details
                        @update:model-value="(val) => handleNoteTypeChange(item.id, val)"
                      />
                    </div>
                    <div class="w-50 d-flex align-end ga-4">
                      <div class="flex-grow-1 mr-4">
                        <v-text-field
                          v-if="newNoteTypes[item.id] === 'Outgoing Email'"
                          v-model="subjectLines[item.id]"
                          label="Email Subject"
                          variant="outlined"
                          density="compact"
                          hide-details
                          placeholder="Enter email subject..."
                          class="mb-4"
                          maxlength="78"
                        />
                        <v-textarea
                          v-model="newNotes[item.id]"
                          label="Add a new note for this user..."
                          rows="3"
                          auto-grow
                          hide-details
                          variant="outlined"
                          density="compact"
                        />
                      </div>
                      <div class="d-flex ga-2 align-self-end">
                        <v-btn
                          color="primary"
                          variant="outlined"
                          :loading="addingNoteState[item.id] === 'note'"
                          :disabled="!newNotes[item.id] || !!addingNoteState[item.id]"
                          @click="handleAddNote(item.id, false)"
                        >
                          Add Note
                        </v-btn>
                        <v-btn
                          v-if="newNoteTypes[item.id] === 'Outgoing Email'"
                          color="primary"
                          variant="flat"
                          :loading="addingNoteState[item.id] === 'email'"
                          :disabled="!newNotes[item.id] || !!addingNoteState[item.id] || !subjectLines[item.id]?.trim()"
                          @click="handleAddNote(item.id, true)"
                        >
                          Add Note & Send Email
                        </v-btn>
                      </div>
                    </div>
                  </div>

                  <v-data-table
                    :headers="headersAdminNotes"
                    :items="item.adminNotes || []"
                    :items-per-page="-1"
                    :sort-by="[{ key: 'createdAt', order: 'desc' }]"
                    hover
                    hide-default-footer
                    class="elevation-1"
                  >
                    <template v-slot:item.type="{ item: note }">
                      <v-chip
                        v-if="note.type"
                        :color="getNoteTypeColor(note.type)"
                        size="small"
                        label
                      >
                        {{ note.type }}
                      </v-chip>
                    </template>
                    <template v-slot:item.emailSent="{ item: note }">
                      <div v-if="note.type === 'Outgoing Email'">
                        <v-icon
                          :color="note.emailSent ? 'success' : 'grey'"
                          size="small"
                        >
                          {{ note.emailSent ? 'mdi-email-check' : 'mdi-email-off-outline' }}
                        </v-icon>
                        <v-tooltip
                          v-if="note.emailSent && note.senderEmail"
                          activator="parent"
                          location="top"
                        >
                          Sent from: {{ note.senderEmail }}
                        </v-tooltip>
                      </div>
                      <span v-else>-</span>
                    </template>
                    <template v-slot:item.subject="{ item: note }">
                      <span v-if="note.subject">{{ note.subject }}</span>
                      <span v-else>-</span>
                    </template>
                    <template v-slot:item.content="{ item: note }">
                      <div class="note-content">{{ note.content }}</div>
                    </template>
                    <template v-slot:item.createdAt="{ item: note }">
                      {{ formatNoteDate(note.createdAt) }}
                    </template>
                    <template v-slot:bottom></template>
                    <template v-slot:no-data>
                      <div class="text-center pa-4">No notes for this user yet.</div>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>
            </td>
          </template>
          <template v-slot:no-data>
            <v-alert v-if="search?.length" color="error" icon="mdi-alert">
              Your search for "{{ search }}" found no results.
            </v-alert>
            <div v-else>
              No data available
            </div>
          </template>
        </v-data-table>
        <div v-if="nextTokenUsers && !loadingUsers" class="text-center pa-4">
          <v-btn @click="handleLoadMoreUsers" :loading="loadingUsers">Load More Users</v-btn>
        </div>

        <v-dialog v-model="deleteDialog" max-width="520">
          <v-card v-if="deleteTarget">
            <v-card-title class="text-error pa-4">Delete user</v-card-title>
            <v-card-text>
              <p class="mb-3">
                Permanently delete <strong>{{ deleteTarget.email }}</strong>? This removes their
                account, org memberships, and project memberships. This cannot be undone.
              </p>
              <div v-if="deleteFootprint.length" class="mb-3">
                <div class="text-caption text-medium-emphasis mb-1">Member of:</div>
                <v-chip
                  v-for="f in deleteFootprint"
                  :key="f.orgId"
                  size="small"
                  class="mr-1 mb-1"
                  :color="f.isSoleOwner ? 'warning' : undefined"
                  variant="tonal"
                >
                  {{ f.orgName }} · {{ f.role }}{{ f.isSoleOwner ? ' (sole owner)' : '' }}
                </v-chip>
              </div>
              <v-alert
                v-if="deleteWouldOrphan"
                type="warning"
                variant="tonal"
                density="compact"
                class="mb-3"
              >
                This user is the sole owner of an org that has other members — deletion will be
                blocked until another owner is assigned.
              </v-alert>
              <v-text-field
                v-model="deleteConfirmText"
                label="Type the email to confirm"
                :placeholder="deleteTarget.email"
                variant="outlined"
                density="compact"
                hide-details
              />
            </v-card-text>
            <v-card-actions class="justify-end pa-4 pt-0">
              <v-btn variant="text" :disabled="deleting" @click="deleteDialog = false">Cancel</v-btn>
              <v-btn
                color="error"
                variant="flat"
                :loading="deleting"
                :disabled="deleteConfirmText !== deleteTarget.email"
                @click="confirmDelete"
              >
                Delete user
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="addAdminDialog" max-width="520">
          <v-card>
            <v-card-title class="pa-4">Add admin</v-card-title>
            <v-card-text>
              <p class="mb-3 text-medium-emphasis">
                Grant platform administrator (SuperAdmin) access. If this email has no account yet,
                one is created and an invitation with temporary credentials is emailed to them.
              </p>
              <v-text-field
                v-model="addAdminEmail"
                label="Email"
                type="email"
                variant="outlined"
                density="compact"
                autofocus
                data-cy="add-admin-email"
                class="mb-3"
                hide-details
                @keyup.enter="confirmAddAdmin"
              />
              <div class="d-flex ga-3">
                <v-text-field
                  v-model="addAdminFirstname"
                  label="First name (optional)"
                  variant="outlined"
                  density="compact"
                  hide-details
                />
                <v-text-field
                  v-model="addAdminLastname"
                  label="Last name (optional)"
                  variant="outlined"
                  density="compact"
                  hide-details
                />
              </div>
            </v-card-text>
            <v-card-actions class="justify-end pa-4 pt-0">
              <v-btn variant="text" :disabled="invitingAdmin" @click="addAdminDialog = false">Cancel</v-btn>
              <v-btn
                color="primary"
                variant="flat"
                :loading="invitingAdmin"
                :disabled="!isValidAdminEmail"
                data-cy="add-admin-submit"
                @click="confirmAddAdmin"
              >
                Add admin
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-window-item>

      <v-window-item v-if="isSuperAdmin" value="organisations">
        <admin-org-management :search="search" />
      </v-window-item>
    </v-window>
  </v-card>
</template>

<script>
import client from '@/graphql/client'
import SendAdminEmail from '@/graphql/mutations/SendAdminEmail'
import store from '@/store'
import { mapState } from 'vuex'
import debounce from '@/utils/debounce'
import { ENTERPRISE_CUSTOMER_ID_PREFIX, OUTGOING_EMAIL_TEMPLATE_KEY } from '@/utils/constants'
import { UserSortableField, SortOrder } from '@/store/user/user'
import AdminOrgManagement from '@/components/AdminOrgManagement.vue'

export default {
  components: {
    AdminOrgManagement
  },
  data () {
    return {
      tab: this.$store.state.isSuperAdmin && !this.$store.state.adminUser ? 'organisations' : 'registeredUsers',
      dialog: [],
      search: '',
      roleFilter: 'ALL',
      orgFilter: null,
      lastLoginFilter: 'ALL',
      roleFilterOptions: [
        { title: 'All roles', value: 'ALL' },
        { title: 'Admin', value: 'Admin' },
        { title: 'Owner', value: 'Owner' },
        { title: 'Member', value: 'Member' },
        { title: 'Viewer', value: 'Viewer' }
      ],
      lastLoginOptions: [
        { title: 'Any time', value: 'ALL' },
        { title: 'Last 7 days', value: '7' },
        { title: 'Last 30 days', value: '30' },
        { title: 'Last 90 days', value: '90' },
        { title: 'Inactive 90+ days', value: 'inactive90' }
      ],
      deleteDialog: false,
      deleteTarget: null,
      deleteConfirmText: '',
      deleting: false,
      deleteFootprint: [],
      addAdminDialog: false,
      addAdminEmail: '',
      addAdminFirstname: '',
      addAdminLastname: '',
      invitingAdmin: false,
      resendingEmail: null,
      footprints: {},
      loadingFootprint: {},
      expanded: [],
      debouncedFetchUsers: null,
      newNotes: {},
      newNoteTypes: {},
      addingNoteState: {},
      subjectLines: {},
      noteTypeOptions: ['Incoming Email', 'Outgoing Email', 'Other'],
      headersAdminNotes: [
        { title: 'Type', key: 'type', sortable: true, width: '10%' },
        { title: 'Sent', key: 'emailSent', sortable: true, width: '6%' },
        { title: 'Subject', key: 'subject', sortable: false, width: '18%' },
        { title: 'Note', key: 'content', sortable: false, width: '32%' },
        { title: 'Author', key: 'author', sortable: true, width: '14%' },
        { title: 'Date', key: 'createdAt', sortable: true, width: '15%' }
      ],
      headersUsers: [
        { title: '', key: 'hasNotes', sortable: false, width: '1%' },
        {
          title: 'Name',
          sortable: false,
          key: 'firstname'
        },
        {
          title: 'Email',
          sortable: false,
          key: 'email'
        },
        {
          title: 'Projects',
          sortable: false,
          key: 'projectsAndMemberships',
          align: 'center'
        },
        {
          title: 'Subscription',
          sortable: false,
          key: 'subscription'
        },
        {
          title: 'Last Login',
          sortable: true,
          key: 'lastLogin',
          width: '10%'
        },
        {
          title: 'Login count',
          sortable: true,
          key: 'loginCounter'
        },
        {
          title: 'Registration Date',
          sortable: true,
          key: 'registrationDate'
        },
        {
          key: 'data-table-expand'
        }
      ],
      headersWorkflows: [
        {
          title: 'Name',
          align: 'left',
          sortable: true,
          key: 'name'
        },
        {
          title: 'Events',
          align: 'right',
          sortable: false,
          key: 'eventsLength'
        }
      ],
      headersProjects: [
        {
          title: 'Project Name',
          sortable: true,
          key: 'name'
        },
        {
          title: 'Organization',
          sortable: true,
          key: 'organisation.name'
        },
        {
          title: 'Workflows',
          sortable: false,
          key: 'workflows'
        }
      ]
    }
  },
  computed: {
    ...mapState({
      users: state => state.user.users,
      platformAdmins: state => state.user.platformAdmins,
      pendingInvites: state => state.user.pendingInvites,
      organisations: state => state.organisations.organisations,
      nextTokenUsers: state => state.user.nextTokenUsers,
      loadingUsers: state => state.user.loadingUsers,
      searchTerm: state => state.user.searchTerm,
      sortBy: state => state.user.sortBy,
      subscriptions: state => state.subscriptions.subscriptions,
      isSuperAdmin: state => state.isSuperAdmin
    }),
    isValidAdminEmail () {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((this.addAdminEmail || '').trim())
    },
    displayHeaders () {
      if (!this.isSuperAdmin) {
        return this.headersUsers
      }
      const headers = [...this.headersUsers]
      const emailIdx = headers.findIndex((h) => h.key === 'email')
      headers.splice(emailIdx + 1, 0, { title: 'Role', sortable: false, key: 'platformRole' })
      const expandIdx = headers.findIndex((h) => h.key === 'data-table-expand')
      headers.splice(expandIdx, 0, { title: '', sortable: false, key: 'actions', width: '48px' })

      return headers
    },
    deleteWouldOrphan () {
      return this.deleteFootprint.some((f) => f.isSoleOwner && f.memberCount > 1)
    },
    roleByEmail () {
      const map = {}
      for (const admin of this.platformAdmins || []) {
        if (admin.email) {
          map[admin.email.toLowerCase()] = admin.role
        }
      }

      return map
    },
    roleById () {
      const map = {}
      for (const admin of this.platformAdmins || []) {
        if (admin.userId) {
          map[admin.userId] = admin.role
        }
      }

      return map
    },
    // userId / lowercased-email -> Set of org roles held across all orgs.
    orgRolesByKey () {
      const map = {}
      const add = (key, role) => {
        if (!key) {
          return
        }
        if (!map[key]) {
          map[key] = new Set()
        }
        map[key].add(role)
      }
      const membersByOrg = this.$store.state.orgMembers.membersByOrg || {}
      for (const orgId of Object.keys(membersByOrg)) {
        for (const m of membersByOrg[orgId]) {
          add(m.userId, m.role)
          add((m.email || '').toLowerCase(), m.role)
        }
      }

      return map
    },
    orgOptions () {
      return (this.organisations || [])
        .map((o) => ({ title: o.name, value: o.id }))
        .sort((a, b) => (a.title || '').localeCompare(b.title || ''))
    },
    orgMemberKeys () {
      if (!this.orgFilter) {
        return null
      }
      const members = this.$store.getters['orgMembers/getMembersByOrgId'](this.orgFilter)
      const set = new Set()
      for (const m of members) {
        if (m.userId) {
          set.add(m.userId)
        }
        if (m.email) {
          set.add(m.email.toLowerCase())
        }
      }

      return set
    },
    anyFilterActive () {
      return this.roleFilter !== 'ALL' || !!this.orgFilter || this.lastLoginFilter !== 'ALL'
    },
    filteredUsers () {
      let list = this.users || []
      if (!this.isSuperAdmin) {
        return list
      }
      if (this.roleFilter !== 'ALL') {
        list = list.filter((u) => this.matchesRole(u, this.roleFilter))
      }
      if (this.orgFilter) {
        list = list.filter((u) => this.userInOrg(u))
      }
      if (this.lastLoginFilter !== 'ALL') {
        list = list.filter((u) => this.matchesLastLogin(u))
      }

      return list
    },
    subscriptionTypeByEmail () {
      if (!this.subscriptions) return {}

      const map = {}
      for (const sub of this.subscriptions) {
        if (!sub.payerUser?.stripeCustomerId) continue

        const customerId = sub.payerUser.stripeCustomerId
        if (customerId.startsWith(ENTERPRISE_CUSTOMER_ID_PREFIX)) {
          map[sub.email] = 'Enterprise'
        } else if (customerId.startsWith('cus_')) {
          map[sub.email] = 'Stripe'
        }
      }
      return map
    },
    tableSort () {
      if (!this.sortBy || !this.sortBy.field) {
        return []
      }

      let tableKey = ''
      switch (this.sortBy.field) {
        case UserSortableField.REGISTRATION_DATE:
          tableKey = 'registrationDate'
          break
        case UserSortableField.LAST_LOGIN:
          tableKey = 'lastLogin'
          break
        case UserSortableField.LOGIN_COUNTER:
          tableKey = 'loginCounter'
          break
        default:
          return []
      }

      return [{ key: tableKey, order: this.sortBy.order.toLowerCase() }]
    }
  },
  methods: {
    async handleAddNote (userId, sendEmail = false) {
      const content = this.newNotes[userId]?.trim()
      const type = this.newNoteTypes[userId] || 'Other'
      if (!content) {
        return
      }

      this.addingNoteState[userId] = sendEmail ? 'email' : 'note'

      const subject = this.subjectLines[userId]?.trim() || null
      const shouldSendEmail = sendEmail && type === 'Outgoing Email'
      const user = this.users.find((u) => u.id === userId)
      let emailSentSuccessfully = false

      try {
        if (shouldSendEmail) {
          if (!user?.email) {
            store.commit('snackbar/showMessage', {
              content: 'User has no email address',
              timeout: 6000,
              color: 'error',
              location: 'bottom right'
            }, { root: true })
            this.addingNoteState[userId] = false
            return
          }

          // Sent server-side from the Continuum support address (the browser no
          // longer holds SES credentials and cannot choose the sender).
          await client.query({
            operationName: 'sendAdminEmail',
            query: SendAdminEmail,
            variables: { recipientEmail: user.email, content, subject }
          })
          emailSentSuccessfully = true

          localStorage.setItem(OUTGOING_EMAIL_TEMPLATE_KEY, JSON.stringify({
            subject,
            content
          }))
        }

        await this.$store.dispatch('user/addAdminNote', {
          userId,
          content,
          type: type || 'Other',
          subject,
          emailSent: shouldSendEmail,
          senderEmail: null
        })

        if (shouldSendEmail) {
          store.commit('snackbar/showMessage', {
            content: `Email sent to ${user.email}`,
            timeout: 4000,
            color: 'success',
            location: 'bottom right'
          }, { root: true })
        }

        this.newNotes[userId] = ''
        this.newNoteTypes[userId] = null
        this.subjectLines[userId] = ''
      } catch (error) {
        let errorMessage = 'Error adding note'
        if (sendEmail) {
          errorMessage = emailSentSuccessfully
            ? 'Email was sent but failed to save note. Please add the note manually.'
            : 'Failed to send email. Note was not saved.'
        }
        store.commit('snackbar/showMessage', {
          content: errorMessage,
          timeout: 6000,
          color: 'error',
          location: 'bottom right'
        }, { root: true })
      } finally {
        this.addingNoteState[userId] = false
      }
    },

    formatNoteDate (isoString) {
      if (!isoString) return ''
      const date = new Date(isoString)
      return date.toLocaleDateString('sv-SE') + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    pendingName (invite) {
      const name = [invite.firstname, invite.lastname].filter(Boolean).join(' ').trim()
      return name || '(no name)'
    },
    async resend (email) {
      this.resendingEmail = email
      try {
        await this.$store.dispatch('user/resendInvite', { email })
      } finally {
        this.resendingEmail = null
      }
    },
    getNoteTypeColor (type) {
      switch (type) {
        case 'Incoming Email':
          return 'blue'
        case 'Outgoing Email':
          return 'green'
        case 'Other':
          return 'grey'
        default:
          return 'secondary'
      }
    },
    // Is the user in any platform-admin Cognito group (SuperAdmin/Admin)?
    isPlatformAdmin (user) {
      return !!(this.roleByEmail[(user.email || '').toLowerCase()] || this.roleById[user.id])
    },
    // Specifically in the SuperAdmin group — drives the promote/demote action.
    isSuperAdminUser (user) {
      const email = (user.email || '').toLowerCase()

      return this.roleByEmail[email] === 'SuperAdmin' || this.roleById[user.id] === 'SuperAdmin'
    },
    // The set of org roles this user holds across all orgs.
    orgRolesFor (user) {
      const roles = new Set()
      const sources = [this.orgRolesByKey[user.id], this.orgRolesByKey[(user.email || '').toLowerCase()]]
      for (const src of sources) {
        if (src) {
          src.forEach((r) => roles.add(r))
        }
      }

      return roles
    },
    // Unified badge: Admin (platform) beats highest org role; null if none.
    effectiveRole (user) {
      if (this.isPlatformAdmin(user)) {
        return 'Admin'
      }
      const roles = this.orgRolesFor(user)
      if (roles.has('OWNER')) {
        return 'Owner'
      }
      if (roles.has('MEMBER')) {
        return 'Member'
      }
      if (roles.has('VIEWER')) {
        return 'Viewer'
      }

      return null
    },
    // Filter match: Admin = platform admin; org roles match if held in any org.
    matchesRole (user, role) {
      if (role === 'Admin') {
        return this.isPlatformAdmin(user)
      }

      return this.orgRolesFor(user).has(role.toUpperCase())
    },
    roleColor (role) {
      if (role === 'Admin') {
        return 'deep-purple-accent-3'
      }
      if (role === 'Owner') {
        return 'success'
      }
      if (role === 'Member') {
        return 'primary'
      }
      if (role === 'Viewer') {
        return 'blue-grey'
      }

      return 'grey'
    },
    userInOrg (user) {
      if (!this.orgMemberKeys) {
        return true
      }

      return this.orgMemberKeys.has(user.id) || this.orgMemberKeys.has((user.email || '').toLowerCase())
    },
    isSelf (user) {
      return user.id === this.$store.state.userId
    },
    async toggleActive (user, isActive) {
      await this.$store.dispatch('user/adminSetUserActive', { userId: user.id, email: user.email, isActive })
    },
    async setRole (user, group, grant) {
      await this.$store.dispatch('user/adminSetPlatformRole', { email: user.email, group, grant })
    },
    async loadFootprint (userId) {
      if (this.footprints[userId] || this.loadingFootprint[userId]) {
        return this.footprints[userId] || []
      }
      this.loadingFootprint[userId] = true
      const footprint = await this.$store.dispatch('user/getUserOrgFootprint', { userId })
      this.footprints[userId] = footprint
      this.loadingFootprint[userId] = false

      return footprint
    },
    async openDeleteDialog (user) {
      this.deleteTarget = user
      this.deleteConfirmText = ''
      this.deleteFootprint = await this.loadFootprint(user.id)
      this.deleteDialog = true
    },
    async confirmDelete () {
      if (!this.deleteTarget || this.deleteConfirmText !== this.deleteTarget.email) {
        return
      }
      this.deleting = true
      const ok = await this.$store.dispatch('user/adminDeleteUser', {
        userId: this.deleteTarget.id,
        email: this.deleteTarget.email
      })
      this.deleting = false
      if (ok) {
        this.deleteDialog = false
      }
    },
    openAddAdminDialog () {
      this.addAdminEmail = ''
      this.addAdminFirstname = ''
      this.addAdminLastname = ''
      this.addAdminDialog = true
    },
    async confirmAddAdmin () {
      if (!this.isValidAdminEmail || this.invitingAdmin) {
        return
      }
      this.invitingAdmin = true
      const ok = await this.$store.dispatch('user/adminInviteSuperAdmin', {
        email: this.addAdminEmail.trim().toLowerCase(),
        firstname: this.addAdminFirstname.trim(),
        lastname: this.addAdminLastname.trim()
      })
      this.invitingAdmin = false
      if (ok) {
        this.addAdminDialog = false
        this.fetchUsers(false)
        this.$store.dispatch('user/listPendingInvites')
      }
    },
    daysSince (iso) {
      return (Date.now() - new Date(iso).getTime()) / 86400000
    },
    matchesLastLogin (user) {
      const ll = user.lastLogin && !user.lastLogin.startsWith('0000') ? user.lastLogin : null
      if (this.lastLoginFilter === 'inactive90') {
        // Never-logged-in users count as inactive.
        return !ll || this.daysSince(ll) > 90
      }
      if (!ll) {
        return false
      }

      return this.daysSince(ll) <= Number(this.lastLoginFilter)
    },
    // Client-side filters only see loaded rows, so when a filter is active we
    // page through the rest of the server-side list to keep results correct.
    async ensureAllUsersLoaded () {
      let guard = 0
      while (this.nextTokenUsers && !this.loadingUsers && guard < 100) {
        guard++
        await this.handleLoadMoreUsers()
      }
    },
    clearFilters () {
      this.roleFilter = 'ALL'
      this.orgFilter = null
      this.lastLoginFilter = 'ALL'
    },
    // Load every org's members so the unified Role badge can resolve each user's
    // org role (Owner/Member/Viewer) across all orgs.
    async loadOrgRoles () {
      await this.$store.dispatch('organisations/listOrganisations')
      const orgs = this.organisations || []
      await Promise.all(orgs.map((o) => this.$store.dispatch('orgMembers/listOrgMembers', { orgId: o.id })))
    },
    toggleRowExpansion (item) {
      const index = this.expanded.findIndex((id) => id === item.id)

      if (index > -1) {
        this.expanded.splice(index, 1)
      } else {
        this.expanded.push(item.id)
      }
    },
    formatProjectsAndMemberships (item) {
      return `${this.countProjects(item.organisations)}`
    },
    gotoWorkflow (workflowId, projectId) {
      const routeData = this.$router.resolve('/workflow/' + projectId + '/' + workflowId)
      window.open(routeData.href, '_blank')
    },
    countProjects (organisations) {
      let res = 0
      for (let i = 0; i < organisations.length; i++) {
        res = res + organisations[i].projects.length
      }
      return res
    },
    getProjects (organisations) {
      const res = []
      for (let i = 0; i < organisations.length; i++) {
        res.push(...organisations[i].projects)
      }
      return res
    },
    mapTableSort (tableSortArray) {
      if (!tableSortArray || tableSortArray.length === 0) {
        return null
      }

      const tableSort = tableSortArray[0]
      let field

      switch (tableSort.key) {
        case 'registrationDate':
          field = UserSortableField.REGISTRATION_DATE
          break
        case 'lastLogin':
          field = UserSortableField.LAST_LOGIN
          break
        case 'loginCounter':
          field = UserSortableField.LOGIN_COUNTER
          break
        default:
          return null
      }

      return {
        field,
        order: tableSort.order.toUpperCase() === 'ASC' ? SortOrder.ASC : SortOrder.DESC
      }
    },

    async handleTableSort (newSortByTableFormat) {
      const sortInput = this.mapTableSort(newSortByTableFormat)

      const variables = {
        search: this.searchTerm,
        append: false,
        fetchAllForSearch: this.searchTerm.length > 0 && this.nextTokenUsers !== null,
        ...(sortInput && { sortBy: sortInput })
      }

      await this.$store.dispatch('user/listUsers', variables)
    },

    async fetchUsers (append = false) {
      const searchTerm = (this.search || '').trim()
      const shouldFetchAll = searchTerm.length > 0 && !append

      await this.$store.dispatch('user/listUsers', {
        search: searchTerm,
        sortBy: this.sortBy,
        append,
        nextToken: append ? this.nextTokenUsers : null,
        fetchAllForSearch: shouldFetchAll
      })
    },

    async handleLoadMoreUsers () {
      if (this.nextTokenUsers && !this.loadingUsers) {
        await this.$store.dispatch('user/listUsers', {
          search: this.searchTerm,
          sortBy: this.sortBy,
          nextToken: this.nextTokenUsers,
          append: true,
          fetchAllForSearch: false
        })
      }
    },

    loadEmailTemplate (userId) {
      const saved = localStorage.getItem(OUTGOING_EMAIL_TEMPLATE_KEY)
      if (saved) {
        try {
          const template = JSON.parse(saved)
          this.subjectLines[userId] = template.subject || ''
          this.newNotes[userId] = template.content || ''
        } catch (e) {
          // Invalid JSON, ignore
        }
      }
    },
    handleNoteTypeChange (userId, newType) {
      if (newType === 'Outgoing Email') {
        this.loadEmailTemplate(userId)
      }
    }
  },
  created () {
    this.debouncedFetchUsers = debounce(() => {
      this.fetchUsers(false)
    }, 500)
  },
  mounted () {
    this.fetchUsers(false)
    this.$store.dispatch('subscriptions/listSubscriptions')
    if (this.isSuperAdmin) {
      this.$store.dispatch('user/listPlatformAdmins')
      this.$store.dispatch('user/listPendingInvites')
      this.loadOrgRoles()
    }
  },
  watch: {
    search (newSearch, oldSearch) {
      if (newSearch !== oldSearch) {
        this.debouncedFetchUsers()
      }
    },
    roleFilter (val) {
      if (val !== 'ALL') {
        this.ensureAllUsersLoaded()
      }
    },
    lastLoginFilter (val) {
      if (val !== 'ALL') {
        this.ensureAllUsersLoaded()
      }
    },
    async orgFilter (val) {
      if (val) {
        await this.$store.dispatch('orgMembers/listOrgMembers', { orgId: val })
        await this.ensureAllUsersLoaded()
      }
    },
    expanded (ids) {
      for (const id of ids) {
        this.loadFootprint(id)
      }
    }
  }
}
</script>

<style scoped>
.card {
  border-radius: 12px;
  background: var(--light-1);
  border: 1px solid var(--light-1);
  padding: 12px 16px 16px !important;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.15);
}

.note-content {
  padding: 8px;
  font-size: 14px;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
